export const FIRM = {
    name: "Nationwide Strategies LLC",
    short: "Nationwide",
    monogram: "NS",
    tagline: "Collection Agency Consulting",
    hq: "Tucson, Arizona",
    coordinates: "32.2226° N / 110.9747° W",
    email: "nwpi257@icloud.com",
    phone: "(520) 527-2900",
    phoneHref: "tel:+15205272900",
    address: "2100 N Wilmot Rd, Suite 316, Tucson, AZ 85730",
};

export const CATEGORIES = [
    "Executive Advisory & Strategy",
    "Compliance & Risk",
    "Operations & Performance",
    "People & Development",
];

export const SERVICES = [
    {
        num: "01",
        title: "Executive & Ownership Advisory",
        category: "Executive Advisory & Strategy",
        description:
            "Confidential, one-to-one consulting for agency owners, chief executives, and senior leaders navigating the decisions that define a firm, from growth and succession to moments of regulatory scrutiny.",
        outcomes: ["Stronger leadership", "Defensible decisions"],
    },
    {
        num: "02",
        title: "Strategic Planning",
        category: "Executive Advisory & Strategy",
        description:
            "Multi-year strategy built to survive contact with market shifts, client concentration, and regulatory change, with priorities sequenced, resourced, and measurable.",
        outcomes: ["Operational performance", "Resilience"],
    },
    {
        num: "03",
        title: "Client & Creditor Relationship Strategy",
        category: "Executive Advisory & Strategy",
        description:
            "Deliberate frameworks for earning and keeping creditor clients: reporting discipline, transparency practices, and performance communication that stands up to audit.",
        outcomes: ["Client confidence", "Accountability"],
    },
    {
        num: "04",
        title: "Leadership Assessment & Succession",
        category: "Executive Advisory & Strategy",
        description:
            "Rigorous assessment of leadership capacity and succession pipelines, aligned to the competencies a compliant, high-performing agency actually needs.",
        outcomes: ["Stronger leadership", "Resilience"],
    },
    {
        num: "05",
        title: "Regulatory Compliance Assessment",
        category: "Compliance & Risk",
        description:
            "System-level examination of FDCPA, Regulation F, TCPA, and state licensing obligations across policies, systems, and daily practice, identifying exposure before examiners or plaintiffs do.",
        outcomes: ["Reduced regulatory risk", "Accountability"],
    },
    {
        num: "06",
        title: "Compliance Management System Design & Review",
        category: "Compliance & Risk",
        description:
            "Design and strengthening of the compliance management system: board oversight, policies, training, monitoring, and complaint response built to supervisory expectations.",
        outcomes: ["Reduced regulatory risk", "Operational performance"],
    },
    {
        num: "07",
        title: "Complaint & Litigation Risk Review",
        category: "Compliance & Risk",
        description:
            "Structured review of complaint patterns, dispute handling, and litigation trends, translating early-warning data into corrective action leadership can defend.",
        outcomes: ["Defensible decisions", "Reduced regulatory risk"],
    },
    {
        num: "08",
        title: "Expert Analysis & Litigation Support",
        category: "Compliance & Risk",
        description:
            "Appropriately qualified expert analysis and litigation support, engaged selectively and within verified professional qualifications and legal standards.",
        outcomes: ["Defensible decisions", "Reduced regulatory risk"],
    },
    {
        num: "09",
        title: "Operational Assessment & Performance Improvement",
        category: "Operations & Performance",
        description:
            "Whole-of-operation assessment covering liquidation, right-party contact, workflow, and cost structure, translated into a sequenced improvement roadmap.",
        outcomes: ["Operational performance", "Higher recovery"],
    },
    {
        num: "10",
        title: "Policy & Procedure Review",
        category: "Operations & Performance",
        description:
            "Comprehensive review of policies, procedures, and call scripts against current law, client requirements, and operational reality, closing the gap between what is written and what is done.",
        outcomes: ["Reduced regulatory risk", "Accountability"],
    },
    {
        num: "11",
        title: "Audit & Certification Readiness",
        category: "Operations & Performance",
        description:
            "Structured preparation for creditor client audits, industry certification, and security assessments, building the operating discipline to sustain readiness beyond the audit.",
        outcomes: ["Operational performance", "Client confidence"],
    },
    {
        num: "12",
        title: "Leadership Development",
        category: "People & Development",
        description:
            "Development architecture for supervisors, managers, and emerging executives: curricula, coaching structures, and accountability rhythms calibrated to collection operations.",
        outcomes: ["Stronger leadership", "Resilience"],
    },
    {
        num: "13",
        title: "Collector Training & Call Quality Assessment",
        category: "People & Development",
        description:
            "Evaluation of collector training, call quality programs, and coaching effectiveness, aligning frontline conduct with compliance standards and performance goals.",
        outcomes: ["Reduced regulatory risk", "Higher recovery"],
    },
    {
        num: "14",
        title: "Workforce Strategy",
        category: "People & Development",
        description:
            "Recruitment, retention, compensation, and incentive strategies that treat staffing as a long-range institutional question rather than a cycle of vacancies.",
        outcomes: ["Resilience", "Operational performance"],
    },
];

export const MARQUEE_ITEMS = [
    "Stronger Leadership",
    "Defensible Decision-Making",
    "Reduced Regulatory Risk",
    "Higher Recovery Rates",
    "Operational Performance",
    "Client Confidence",
];

export const CHAPTERS = [
    {
        num: "01",
        title: "Principal-Led Integrity",
        body: "Every engagement is led by a principal with agency leadership experience, not delegated, not staffed down. The consulting you receive comes from people who have carried the responsibility themselves.",
    },
    {
        num: "02",
        title: "Defensible Decision-Making",
        body: "We build decisions that can be explained, to a regulator, a court, a client, and a consumer. Evidence over anecdote, documentation over recollection, structure over instinct.",
    },
    {
        num: "03",
        title: "A Hybrid Model, Deliberately Scaled",
        body: "A boutique core, selectively expanded with senior subject-matter experts when an engagement demands it. You get depth without a consulting pyramid, in Arizona and nationally.",
    },
    {
        num: "04",
        title: "Institutional Discretion",
        body: "Client identities, engagement details, and findings are never disclosed. References are furnished only with client consent, and credentials are published only when verified.",
    },
];

export const OUTCOME_ROWS = [
    { index: "O.01", label: "Regulatory posture", note: "Decisions and practices documented to withstand examination and litigation" },
    { index: "O.02", label: "Compliance risk", note: "Exposure identified at the system level, before it compounds" },
    { index: "O.03", label: "Liquidation performance", note: "Recovery operations measured, coached, and improved" },
    { index: "O.04", label: "Client confidence", note: "Reporting and audit readiness that retain creditor relationships" },
    { index: "O.05", label: "Leadership bench", note: "Succession and development treated as institutional strategy" },
    { index: "O.06", label: "Resilience", note: "The capacity to absorb regulatory change and emerge stronger" },
];

export const METHOD_STEPS = [
    { num: "01", title: "Assess", body: "Independent examination of structure, policy, practice, and data. We begin from evidence, not assumption." },
    { num: "02", title: "Strategy", body: "Findings translated into a sequenced, resourced course of action with clear ownership and measures." },
    { num: "03", title: "Execution", body: "Embedded support through implementation: briefings, coaching, policy drafting, and course correction." },
    { num: "04", title: "Verification", body: "Progress measured against the standard set at the outset, so improvement is demonstrable, not asserted." },
];

export const CASE_ARCHETYPES = [
    {
        title: "Exam-Ready in a Quarter",
        context: "Mid-size collection agency · Southwest",
        body: "Ahead of a regulatory examination, a compliance management system review closed gaps in oversight, training documentation, and complaint response, and rebuilt the evidence file examiners actually ask for.",
        result: "Examination posture established on schedule; findings closed.",
    },
    {
        title: "Complaint Curve, Bent",
        context: "National receivables firm",
        body: "A structured review of complaint and dispute patterns traced recurring friction to a handful of scripts and letter workflows, producing targeted corrections and a monitoring rhythm leadership owns.",
        result: "Complaint trend reversed within two quarters.",
    },
    {
        title: "Audits as an Asset",
        context: "Regional agency · creditor clients",
        body: "Audit readiness work moved an agency from scrambling before each creditor audit to a standing evidence practice, strengthening client retention in the process.",
        result: "Client audit cycle completed without findings escalation.",
    },
];

export const INSIGHTS = [
    {
        slug: "regulation-f-operating-rhythm",
        title: "Regulation F as an Operating Rhythm, Not a Rulebook",
        category: "Compliance & Risk",
        date: "June 2026",
        readTime: "6 min read",
        featured: true,
        excerpt:
            "Most agencies can recite Regulation F. Far fewer have built it into the daily rhythm of the floor. The distance between the two is where examinations are lost.",
        body: [
            {
                heading: "The rulebook on the shelf",
                paragraphs: [
                    "Nearly every collection agency owns an accurate summary of Regulation F. Policies quote it, trainers reference it, and leadership believes it is handled. Yet examiners and plaintiff attorneys do not evaluate what an agency knows; they evaluate what its collectors do at 2:00 in the afternoon on a difficult call.",
                    "Compliance fails in the gap between documentation and habit. A policy that is correct but unpracticed is not a control; it is an artifact. The agencies that perform well under scrutiny are the ones that converted the rulebook into routines: call calibration sessions, model notice reviews, and supervisory checks that happen on a schedule rather than on a hunch.",
                ],
            },
            {
                heading: "Rhythm beats memory",
                paragraphs: [
                    "An operating rhythm means the compliance activity has a cadence: weekly call reviews, monthly dispute-trend analysis, quarterly policy attestations. When the cadence exists, individual turnover and forgetfulness stop mattering, because the system carries the standard.",
                    "This is unglamorous work. It is also the only work that shows up in an examination. Regulators consistently reward agencies whose monitoring is boring, regular, and documented over agencies whose compliance is brilliant but episodic.",
                ],
            },
            {
                heading: "Evidence is the product",
                paragraphs: [
                    "Every control should end in a record: what was reviewed, what was found, what was corrected, and who verified the correction. An agency that can produce that chain on demand is not merely compliant; it is credible, and credibility is what shortens examinations and settles disputes early.",
                    "Build the rhythm first. The rulebook will take care of itself.",
                ],
            },
        ],
    },
    {
        slug: "complaints-early-warning",
        title: "Complaints Are an Early-Warning System",
        category: "Compliance & Risk",
        date: "May 2026",
        readTime: "5 min read",
        featured: false,
        excerpt:
            "A complaint is rarely about one consumer or one call. Read correctly, complaint data is the cheapest consulting engagement an agency will ever get.",
        body: [
            {
                heading: "The signal in the noise",
                paragraphs: [
                    "Agencies tend to treat complaints as isolated fires: resolve the consumer, close the ticket, move on. That instinct wastes the most valuable compliance data the business produces. A complaint is a consumer telling you, for free, exactly where your operation creates friction.",
                    "Aggregated over a quarter, complaint patterns point with unusual precision at the script, letter, workflow, or collector group that generates risk. The agencies that get sued are rarely surprised; the pattern was in the inbox months earlier.",
                ],
            },
            {
                heading: "Categorize, then act",
                paragraphs: [
                    "Useful complaint programs share three traits: consistent categorization by root cause rather than by channel, a named owner for each category, and a monthly review where leadership looks at trends rather than anecdotes.",
                    "The output is not a report; it is a short list of corrective actions with dates. A complaint program that does not change operations is customer service, not risk management.",
                ],
            },
            {
                heading: "Closing the loop",
                paragraphs: [
                    "The final discipline is verification. Sixty days after a corrective action, pull the data again and ask whether the pattern actually moved. Agencies that close this loop find that complaint volume becomes one of the most honest performance metrics they have.",
                ],
            },
        ],
    },
    {
        slug: "liquidation-leadership-metric",
        title: "Liquidation Is a Leadership Metric",
        category: "Operations & Performance",
        date: "April 2026",
        readTime: "6 min read",
        featured: false,
        excerpt:
            "Recovery curves do not improve because collectors are told to collect more. They improve when leadership fixes the system collectors work inside.",
        body: [
            {
                heading: "The floor reflects the front office",
                paragraphs: [
                    "When liquidation lags, the instinct is to push the floor: more dials, tighter monitoring, louder scoreboards. Sometimes effort is the problem. More often, the system is: stale data, poorly worked segments, wrong-hour calling strategies, and training that ended on a collector's first week.",
                    "Collectors can only perform inside the system they are given. Leadership owns the system.",
                ],
            },
            {
                heading: "Measure the system, coach the collector",
                paragraphs: [
                    "The most useful performance reviews separate collector skill from system quality. Right-party contact rates by segment reveal data problems. Conversion on contact reveals skill and coaching problems. Promise-kept rates reveal workflow problems. Each has a different owner and a different fix.",
                    "Agencies that make this separation stop arguing about effort and start fixing inputs, which is where durable gains live.",
                ],
            },
            {
                heading: "Sustaining the gain",
                paragraphs: [
                    "Improvement initiatives fail at the ninety-day mark, when attention moves on. The agencies that hold gains assign each improvement an owner, a metric, and a recurring review, then treat regression as a leadership issue rather than a floor issue.",
                ],
            },
        ],
    },
    {
        slug: "audit-readiness-retention",
        title: "Audit Readiness Is a Retention Strategy",
        category: "Executive Advisory & Strategy",
        date: "March 2026",
        readTime: "5 min read",
        featured: false,
        excerpt:
            "Creditor audits are usually treated as an interruption. Agencies that treat them as a sales asset keep clients longer and win better placements.",
        body: [
            {
                heading: "The audit is the relationship",
                paragraphs: [
                    "To a creditor, the audit is how they decide whether to keep placing business. An agency that scrambles for two weeks before each audit is telling the client that compliance is an event. An agency that hands over a current evidence file in a day is telling the client something else entirely.",
                    "Clients renew with agencies they do not have to worry about. Audit performance is therefore not a compliance topic; it is a revenue topic.",
                ],
            },
            {
                heading: "A standing evidence practice",
                paragraphs: [
                    "Readiness is built between audits, not before them. Licensing files, training logs, call monitoring summaries, complaint trends, and corrective-action records should be current at all times, organized the way an auditor will ask for them.",
                    "The test is simple: if a client announced an audit tomorrow, would anyone in your agency work late tonight?",
                ],
            },
            {
                heading: "From scramble to standard",
                paragraphs: [
                    "Agencies that reach a standing-ready posture describe the same side effect: the discipline built for auditors improves daily operations. Evidence practices surface problems early, and the confidence of a clean audit becomes part of every new-business conversation.",
                ],
            },
        ],
    },
];

export const NAICS_CODES = [
    { code: "541611", title: "Administrative & General Management Consulting Services" },
    { code: "541618", title: "Other Management Consulting Services" },
    { code: "611430", title: "Professional & Management Development Training" },
    { code: "541690", title: "Other Scientific & Technical Consulting Services" },
];

export const DIFFERENTIATORS = [
    {
        title: "Principal-led, always",
        body: "Every engagement is directed by a founding executive with more than three decades of receivables industry leadership experience. No handoffs to junior staff.",
    },
    {
        title: "Hybrid scale, on demand",
        body: "A boutique core that expands selectively with vetted senior subject-matter experts, providing depth without the overhead of a consulting pyramid.",
    },
    {
        title: "Arizona-rooted, nationally engaged",
        body: "Headquartered in Tucson with deep familiarity in Arizona's business and regulatory landscape, and structured for selective national engagements.",
    },
    {
        title: "Confidentiality as policy",
        body: "Client identities and findings are never disclosed. References and credentials are furnished only when verified and only with consent.",
    },
];

export const CONTRACT_VEHICLES = [
    { title: "Direct Award & Sole Source", note: "Available for state, local, and tribal engagements where permitted" },
    { title: "Task-Order / IDIQ Arrangements", note: "Structured for on-call advisory and assessment support" },
    { title: "Cooperative Purchasing", note: "Evaluation of applicable Arizona cooperative vehicles in progress" },
    { title: "Federal Schedules", note: "Prospective, to be pursued as federal engagement demand warrants" },
];

export const PRINCIPLES = [
    {
        title: "Confidentiality first",
        body: "Engagement details, client identities, and findings remain protected. What we learn belongs to the client.",
    },
    {
        title: "Evidence over anecdote",
        body: "Assessments begin from data, documents, and direct observation, not from the loudest account in the room.",
    },
    {
        title: "Independence",
        body: "We owe our findings to the truth of the organization, not to the preference of any stakeholder within it.",
    },
    {
        title: "Measured consulting",
        body: "We recommend what the organization can actually execute, sequenced to its capacity, not what looks impressive in a report.",
    },
    {
        title: "No unsupported claims",
        body: "Credentials, references, and qualifications are published only when verified. This standard applies to ourselves first.",
    },
];

export const GLOSSARY = [
    {
        index: "G.01",
        term: "FDCPA",
        expansion: "Fair Debt Collection Practices Act",
        body: "The federal law that governs how third-party collectors may communicate with consumers: when they may call, what they must disclose, and what conduct is prohibited. It is the foundation most consumer lawsuits are built on.",
    },
    {
        index: "G.02",
        term: "Regulation F",
        expansion: "The CFPB's debt collection rule",
        body: "The Consumer Financial Protection Bureau's detailed rulebook implementing the FDCPA: call frequency limits, model validation notices, and rules for emails, texts, and voicemails. In practice, it is the standard examiners measure agencies against.",
    },
    {
        index: "G.03",
        term: "TCPA",
        expansion: "Telephone Consumer Protection Act",
        body: "The federal law governing autodialed calls, prerecorded messages, and texts to cell phones. Consent language and revocation tracking are where most TCPA exposure begins, and where disciplined record-keeping ends it.",
    },
    {
        index: "G.04",
        term: "State Licensing",
        expansion: "Arizona & multi-state requirements",
        body: "Most states separately license or register collection agencies, and the requirements, bonds, and renewal cycles differ state by state. Arizona licensure covers activity here, but agencies collecting across state lines carry a patchwork of obligations that lapses quietly and expensively if no one owns the calendar.",
    },
];

export const FAQS = [
    {
        q: "What does an engagement typically cost?",
        a: "Defined assessments are fixed-fee, scoped and priced in writing before any work begins. Ongoing executive advisory is a flat monthly arrangement. There are no hourly meters and no surprise invoices, and the initial conversation costs nothing.",
    },
    {
        q: "How long does a typical engagement take?",
        a: "A focused compliance or operations assessment usually runs four to eight weeks. Audit and certification readiness programs typically run three to six months. Advisory relationships are ongoing and scale up or down with your calendar.",
    },
    {
        q: "Will our information stay confidential?",
        a: "Completely. We publish no client list, display no logos, and never reference an engagement externally. Findings belong to you alone, and a mutual non-disclosure agreement is available before any documents change hands.",
    },
    {
        q: "Do you work with agencies outside Arizona?",
        a: "Yes. The firm is headquartered in Tucson and accepts selective national engagements where the mandate fits the practice. Most work is delivered effectively in a hybrid of on-site visits and remote sessions.",
    },
    {
        q: "Are you attorneys? Is this legal advice?",
        a: "No. Nationwide Strategies is a consulting firm, not a law firm, and nothing we provide is legal advice. We work alongside your legal team, and expert analysis is offered only within verified professional qualifications.",
    },
    {
        q: "Do you sell software or earn referral fees from vendors?",
        a: "No. We take no commissions, referral fees, or resale margins from any technology or service vendor. When we recommend a change, it is because the operation needs it, and that independence is contractual.",
    },
    {
        q: "What do you need from us to get started?",
        a: "A conversation. After an initial confidential call, you receive a written scope with deliverables, timeline, and a fixed price. You decide from there, with no obligation created by the inquiry itself.",
    },
];
